/**
 * Mock Speeds (package N3): Time's ladder, a seeded Speed price rule, and the
 * Speeds a preview offers, derived from the price options the mock already
 * records.
 *
 * This is a fake for journey iteration, not the backend's policy: it follows
 * the rules the backend states (spec docs/features/v1-completion/N_time_spec.md
 * §3.3-§3.5) closely enough for the pages to show every state — a Speed's
 * pool is every option meeting it, the pick is the cheapest with delivery
 * (in-house provisionally while its delivery is unpriced), faster is never
 * cheaper, the default is Standard (or the cheapest in time for a needed-by
 * date) — and words dates the way Time does. Dates count weekdays only.
 */

import { addBusinessDays, differenceInBusinessDays, format } from "date-fns";

import type { CataloguePriceOption } from "../data/supplier-pricing-types";
import type {
  OptionArrival,
  SpeedChoice,
  SpeedLadderRung,
  SpeedOffer,
  SpeedPriceRule,
  UnavailableSpeed,
} from "../data/speed-types";
import { currentTime } from "../domain/current-time";

/** Time's ladder (`speeds.yaml`, d57), slowest first. */
export const MOCK_SPEED_LADDER: SpeedLadderRung[] = [
  {
    speedCode: "economy",
    label: "Economy",
    workingDaysToArrival: 7,
    needsAPerson: false,
  },
  {
    speedCode: "standard",
    label: "Standard",
    workingDaysToArrival: 3,
    needsAPerson: false,
  },
  {
    speedCode: "express",
    label: "Express",
    workingDaysToArrival: 2,
    needsAPerson: false,
  },
  {
    speedCode: "next_day",
    label: "Next day",
    workingDaysToArrival: 1,
    needsAPerson: true,
  },
  {
    speedCode: "same_day",
    label: "Same day",
    workingDaysToArrival: 0,
    needsAPerson: true,
  },
];

export const MOCK_BASELINE_SPEED = "standard";

/** The mock price groups' rule: what the mock-up shows (Express +15%, at least £10). */
export const MOCK_SPEED_PRICE_RULE: SpeedPriceRule = {
  entries: [
    {
      speedCode: "express",
      upliftPct: 0.15,
      minimumUpliftPence: 1000,
      maximumUpliftPence: null,
    },
    {
      speedCode: "next_day",
      upliftPct: 0.3,
      minimumUpliftPence: 2500,
      maximumUpliftPence: null,
    },
    {
      speedCode: "same_day",
      upliftPct: 0.5,
      minimumUpliftPence: 5000,
      maximumUpliftPence: null,
    },
  ],
};

const speedIndex = (code: string | null) =>
  code == null ? -1 : MOCK_SPEED_LADDER.findIndex((s) => s.speedCode === code);

function today(): Date {
  const now = currentTime();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function iso(day: Date): string {
  return format(day, "yyyy-MM-dd");
}

/** Time's short date: "Tue 20 Oct". */
export function mockShortDate(isoDay: string): string {
  return format(new Date(`${isoDay}T00:00:00`), "EEE d MMM");
}

/** The fastest Speed an arrival `workingDays` from today meets, or null. */
export function mockSpeedCodeFor(workingDays: number): string | null {
  const met = MOCK_SPEED_LADDER.filter(
    (s) => workingDays <= s.workingDaysToArrival,
  );
  return met.length > 0 ? met[met.length - 1].speedCode : null;
}

/** An arrival `workingDays` from today, worded as Time words it. */
export function mockArrival(
  workingDays: number,
  options: {
    dateFrom: string;
    isEstimate?: boolean;
    bufferDays?: number;
    neededBy?: string | null;
  },
): OptionArrival {
  const arrives = addBusinessDays(today(), workingDays);
  const withoutBuffer = addBusinessDays(arrives, -(options.bufferDays ?? 1));
  const arrivesOn = iso(arrives);
  const neededBy = options.neededBy ?? null;
  const approveBy =
    neededBy != null
      ? iso(
          addBusinessDays(
            new Date(`${neededBy}T00:00:00`),
            -Math.max(workingDays, 0),
          ),
        )
      : null;
  const approveByOrToday =
    approveBy != null && approveBy > iso(today()) ? approveBy : null;
  return {
    arrivesOn,
    arrivesBy: null,
    arrivesWithoutBufferOn: iso(withoutBuffer),
    isEstimate: options.isEstimate ?? false,
    basis: approveByOrToday ? "assumed_approve_by" : "assumed_today",
    approveBy,
    daysToSpare:
      neededBy != null
        ? differenceInBusinessDays(new Date(`${neededBy}T00:00:00`), arrives)
        : null,
    sentence: `${options.isEstimate ? "Usually arrives" : "Arrives"} ${mockShortDate(arrivesOn)}`,
    dateFrom: options.dateFrom,
    approveBySentence:
      approveBy != null
        ? `Approve the proof ${approveByOrToday ? `by ${mockShortDate(approveBy)}` : "today"} to arrive ${mockShortDate(arrivesOn)}`
        : null,
  };
}

export interface MockSpeedRequest {
  speedCode?: string | null;
  neededBy?: string | null;
  neededByIsHard?: boolean;
}

interface Pick {
  option: CataloguePriceOption;
  reason: string;
  isProvisional: boolean;
}

export interface MockSpeedOutcome {
  speeds: SpeedChoice;
  /** Each offered Speed's option and why: the selected one's is the default option. */
  picks: Record<string, Pick>;
}

function comparableOf(o: CataloguePriceOption): number {
  return o.comparablePence ?? o.pricePence;
}

function pickFromPool(pool: CataloguePriceOption[], speedCode: string): Pick {
  const inTime = pool.filter((o) => o.arrivesInTime);
  const candidates = inTime.length > 0 ? inTime : pool;
  const inHouse = candidates.filter((o) => o.routeClass === "in_house");
  const deliveryPending = inHouse.some((o) => o.delivery.kind === "at_address");
  const byPrice = (measure: (o: CataloguePriceOption) => number) =>
    [...candidates].sort(
      (a, b) =>
        measure(a) - measure(b) ||
        Number(a.routeClass !== "in_house") -
          Number(b.routeClass !== "in_house") ||
        Number(a.speedCode !== speedCode) - Number(b.speedCode !== speedCode),
    );
  if (
    deliveryPending &&
    inHouse.length > 0 &&
    candidates.some((o) => o.routeClass === "bought_in")
  ) {
    const option = [...inHouse].sort((a, b) => a.pricePence - b.pricePence)[0];
    return {
      option,
      reason: "The cheapest in-house option, until the delivery address is set",
      isProvisional: true,
    };
  }
  const option = byPrice(comparableOf)[0];
  const withDelivery =
    option.delivery.kind === "priced" || option.delivery.kind === "included"
      ? " with delivery"
      : "";
  return {
    option,
    reason: `Lowest price${withDelivery}`,
    isProvisional: false,
  };
}

function floorPence(
  rule: SpeedPriceRule,
  speedCode: string,
  basePence: number,
): { floor: number; note: string | null } {
  const entry = rule.entries.find((e) => e.speedCode === speedCode);
  if (!entry || entry.upliftPct === 0) return { floor: basePence, note: null };
  let uplift = Math.round(basePence * entry.upliftPct);
  if (entry.minimumUpliftPence != null)
    uplift = Math.max(uplift, entry.minimumUpliftPence);
  if (entry.maximumUpliftPence != null)
    uplift = Math.min(uplift, entry.maximumUpliftPence);
  const min =
    entry.minimumUpliftPence != null
      ? `, at least £${(entry.minimumUpliftPence / 100).toFixed(2)}`
      : "";
  return {
    floor: basePence + uplift,
    note: `+${Math.round(entry.upliftPct * 100)}% over Standard${min}`,
  };
}

/** The Speeds the options offer, the default, and the one selected. */
export function mockSpeedChoice(
  options: CataloguePriceOption[],
  req: MockSpeedRequest,
  rule: SpeedPriceRule = MOCK_SPEED_PRICE_RULE,
): MockSpeedOutcome {
  const neededBy = req.neededBy ?? null;
  const offersBefore: { rung: SpeedLadderRung; pick: Pick }[] = [];
  const unavailable: UnavailableSpeed[] = [];
  MOCK_SPEED_LADDER.forEach((rung, i) => {
    const pool = options.filter(
      (o) => o.arrival != null && speedIndex(o.speedCode) >= i,
    );
    if (pool.length === 0) {
      unavailable.push({
        speedCode: rung.speedCode,
        label: rung.label,
        reason:
          rung.workingDaysToArrival === 0
            ? "No option arrives the same working day"
            : `No option arrives within ${rung.workingDaysToArrival} working day${rung.workingDaysToArrival === 1 ? "" : "s"}`,
      });
      return;
    }
    offersBefore.push({ rung, pick: pickFromPool(pool, rung.speedCode) });
  });
  const baseline =
    offersBefore.find((o) => o.rung.speedCode === MOCK_BASELINE_SPEED) ??
    offersBefore[0];
  const baselineIndex = baseline ? speedIndex(baseline.rung.speedCode) : -1;
  let previousFloor = 0;
  const picks: Record<string, Pick> = {};
  const offers: SpeedOffer[] = offersBefore.map(({ rung, pick }) => {
    picks[rung.speedCode] = pick;
    const own = pick.option.pricePence;
    let price = own;
    let note: string | null = null;
    if (baseline && speedIndex(rung.speedCode) > baselineIndex) {
      const { floor, note: ruleNote } = floorPence(
        rule,
        rung.speedCode,
        baseline.pick.option.pricePence,
      );
      const floorHere = Math.max(floor, previousFloor);
      previousFloor = floorHere;
      if (floorHere > own) {
        price = floorHere;
        note = ruleNote ?? "never less than a slower Speed";
      }
    }
    const arrival = pick.option.arrival!;
    const tight =
      neededBy != null &&
      (req.neededByIsHard ?? false) &&
      (arrival.daysToSpare ?? 1) <= 0;
    const delivery = pick.option.delivery;
    return {
      speedCode: rung.speedCode,
      label: rung.label,
      workingDaysToArrival: rung.workingDaysToArrival,
      optionKey: pick.option.key,
      arrival,
      pricePence: price,
      comparablePence:
        delivery.kind === "priced"
          ? price + delivery.deliveryPricePence
          : delivery.kind === "at_address"
            ? null
            : price,
      upliftPence: price - own,
      ruleNote: note,
      isToBeConfirmed: rung.needsAPerson || tight,
      arrivesInTime: pick.option.arrivesInTime,
      sentence: `${rung.label} · ${arrival.isEstimate ? "usually arrives" : "arrives"} ${mockShortDate(arrival.arrivesOn)}`,
    };
  });
  const [defaultCode, defaultReason] = chooseDefault(offers, neededBy);
  const requested = req.speedCode ?? null;
  const selectedCode =
    requested != null && offers.some((o) => o.speedCode === requested)
      ? requested
      : defaultCode;
  const selectedAt = speedIndex(selectedCode);
  return {
    speeds: {
      offers,
      unavailable,
      baselineCode: baseline?.rung.speedCode ?? null,
      defaultCode,
      defaultReason,
      selectedCode,
      quickerCode:
        offers.find((o) => speedIndex(o.speedCode) > selectedAt)?.speedCode ??
        null,
      neededBy,
      neededByIsHard: req.neededByIsHard ?? false,
    },
    picks,
  };
}

function chooseDefault(
  offers: SpeedOffer[],
  neededBy: string | null,
): [string | null, string] {
  if (offers.length === 0) return [null, "No Speed can be offered"];
  if (neededBy != null) {
    const inTime = offers.filter((o) => o.arrivesInTime);
    if (inTime.length > 0) {
      const pick = [...inTime].sort(
        (a, b) =>
          a.pricePence - b.pricePence ||
          speedIndex(a.speedCode) - speedIndex(b.speedCode),
      )[0];
      return [
        pick.speedCode,
        `The cheapest Speed that arrives by ${mockShortDate(neededBy)}`,
      ];
    }
    return [
      offers[offers.length - 1].speedCode,
      `Nothing arrives by ${mockShortDate(neededBy)}, so the fastest Speed`,
    ];
  }
  const standard = offers.find((o) => o.speedCode === MOCK_BASELINE_SPEED);
  if (standard) return [standard.speedCode, "Standard"];
  const at = speedIndex(MOCK_BASELINE_SPEED);
  const nearest = [...offers].sort(
    (a, b) =>
      Math.abs(speedIndex(a.speedCode) - at) -
        Math.abs(speedIndex(b.speedCode) - at) ||
      speedIndex(a.speedCode) - speedIndex(b.speedCode),
  )[0];
  return [
    nearest.speedCode,
    `Standard is not offered for this item, so the nearest Speed, ${nearest.label}`,
  ];
}

/** Whether an option arriving `workingDays` from today reaches a needed-by date. */
export function mockArrivesInTime(
  workingDays: number,
  neededBy: string | null | undefined,
): boolean {
  if (!neededBy) return true;
  return iso(addBusinessDays(today(), workingDays)) <= neededBy;
}

/**
 * The in-house route's options at one price (in-house is just another
 * supplier, d23): its time file's tiers, the delivery leg the usual
 * delivery's estimate until the address is known.
 */
export function mockInHouseOptions(
  pricePence: number,
  delivery:
    | { kind: "priced"; deliveryPricePence: number }
    | { kind: "at_address" }
    | { kind: "none" },
  neededBy: string | null | undefined,
): CataloguePriceOption[] {
  const tiers: [string, number][] = [
    ["TopPriority", 3],
    ["Express", 5],
    ["Standard", 7],
    ["Relaxed", 9],
  ];
  return tiers.map(([tier, days]) => {
    const isEstimate = delivery.kind === "at_address";
    const arrival = mockArrival(days, {
      dateFrom:
        delivery.kind === "none"
          ? "Our table + 1 working day buffer"
          : isEstimate
            ? "Our table + 1 working day buffer + the usual delivery"
            : "Our table + 1 working day buffer + 1 working day delivery",
      isEstimate,
      neededBy,
    });
    return {
      key: `in_house:${tier}`,
      routeCode: "in_house",
      routeClass: "in_house",
      routeLabel: "In-house",
      tier,
      arrival,
      speedCode: mockSpeedCodeFor(days),
      noArrivalReason: null,
      pricePence,
      costPence: null,
      contributionPct: null,
      delivery,
      comparablePence:
        delivery.kind === "priced"
          ? pricePence + delivery.deliveryPricePence
          : delivery.kind === "none"
            ? pricePence
            : null,
      isExpiring: false,
      arrivesInTime: mockArrivesInTime(days, neededBy),
    };
  });
}

/** The day a Speed arrives, from today (the date the customer sees, ISO), or null for a Speed not on the ladder. */
export function mockArrivalOn(speedCode: string): string | null {
  const rung = MOCK_SPEED_LADDER.find((s) => s.speedCode === speedCode);
  return rung ? iso(addBusinessDays(today(), rung.workingDaysToArrival)) : null;
}

/**
 * The earliest a Speed could arrive, before the supplier's buffer (ISO): the mock's buffer is one working day, and
 * never takes the date before today, where it reads as one date. Null for a Speed not on the ladder.
 */
export function mockEarliestArrivalOn(speedCode: string): string | null {
  const rung = MOCK_SPEED_LADDER.find((s) => s.speedCode === speedCode);
  if (!rung) return null;
  const earliest = addBusinessDays(
    addBusinessDays(today(), rung.workingDaysToArrival),
    -1,
  );
  return iso(earliest < today() ? today() : earliest);
}

/**
 * A Speed with its date, as Time words it, arriving within the Speed's own
 * days from today; `estimate` before the delivery address is known.
 */
export function mockSpeedSentence(
  speedCode: string,
  estimate = false,
): string | null {
  const rung = MOCK_SPEED_LADDER.find((s) => s.speedCode === speedCode);
  if (!rung) return null;
  const on = iso(addBusinessDays(today(), rung.workingDaysToArrival));
  return `${rung.label} · ${estimate ? "usually arrives" : "arrives"} ${mockShortDate(on)}`;
}

/** A preview that offers no Speed (stories whose subject is not the Speeds). */
export const MOCK_NO_SPEEDS: SpeedChoice = {
  offers: [],
  unavailable: [],
  baselineCode: null,
  defaultCode: null,
  defaultReason: "No Speed can be offered",
  selectedCode: null,
  quickerCode: null,
  neededBy: null,
  neededByIsHard: false,
};
